import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Image as ImageIcon, X } from "lucide-react";

const STATUS = ["Requested","Quote Requested","Quote Uploaded","Awaiting Approval","Scheduled","In Progress","Completed","Under Review","Closed","Cancelled"];

export default function WorkOrders() {
  const [params] = useSearchParams();
  const filter = params.get("filter") || "all";
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ description:"", priority:"Medium", asset_id:"", assigned_supplier_id:"", is_regulatory:false, required_completion_date:"" });
  const [files, setFiles] = useState([]);

  const load = useCallback(async () => {
    const p = filter==="all"?{}:{filter};
    const { data } = await api.get("/work-orders", { params: p });
    setRows(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/assets").then((r)=>setAssets(r.data));
    api.get("/suppliers").then((r)=>setSuppliers(r.data));
  }, []);

  const save = async () => {
    try {
      const payload = { ...f };
      if (!payload.asset_id) delete payload.asset_id;
      if (!payload.assigned_supplier_id) delete payload.assigned_supplier_id;
      if (!payload.required_completion_date) delete payload.required_completion_date;
      const { data } = await api.post("/work-orders", payload);
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((file) => fd.append("files", file));
        try { await api.post(`/work-orders/${data.id}/photos`, fd); }
        catch { toast.error("Photos failed to upload"); }
      }
      toast.success("Work order created"); setOpen(false);
      setFiles([]); setF({ description:"", priority:"Medium", asset_id:"", assigned_supplier_id:"", is_regulatory:false, required_completion_date:"" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (id, s) => {
    const patch = { status: s };
    if (s==="Completed") patch.updated_at = new Date().toISOString();
    try { await api.put(`/work-orders/${id}`, patch); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="work-orders-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Work</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Work Orders</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-wo-btn"><Plus className="w-4 h-4 mr-1"/>New work order</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create work order</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Description *</Label><Textarea value={f.description} onChange={(e)=>setF({...f, description:e.target.value})} data-testid="new-wo-desc"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Asset</Label>
                  <Select value={f.asset_id} onValueChange={(v)=>setF({...f, asset_id:v})}>
                    <SelectTrigger><SelectValue placeholder="Optional"/></SelectTrigger>
                    <SelectContent className="max-h-72">{assets.map((a)=><SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Supplier</Label>
                  <Select value={f.assigned_supplier_id} onValueChange={(v)=>setF({...f, assigned_supplier_id:v})}>
                    <SelectTrigger><SelectValue placeholder="Optional"/></SelectTrigger>
                    <SelectContent className="max-h-72">{suppliers.map((s)=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Priority</Label>
                  <Select value={f.priority} onValueChange={(v)=>setF({...f, priority:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{["Critical","High","Medium","Low"].map((p)=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Required by</Label><Input type="date" value={f.required_completion_date} onChange={(e)=>setF({...f, required_completion_date:e.target.value})}/></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={f.is_regulatory} onCheckedChange={(v)=>setF({...f, is_regulatory:!!v})} data-testid="new-wo-regulatory"/>
                Regulatory / compliance driven
              </label>
              <div>
                <Label>Photos / documents (optional)</Label>
                <input type="file" multiple accept="image/*,application/pdf"
                  onChange={(e)=>setFiles(Array.from(e.target.files||[]))}
                  className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-slate-200 file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100"
                  data-testid="wo-photos"/>
                {files.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {files.map((file, i)=>(
                      <div key={`${file.name}-${file.size}-${i}`} className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1 flex items-center gap-1">
                        <ImageIcon className="w-3 h-3"/><span className="truncate max-w-[10rem]">{file.name}</span>
                        <button type="button" className="text-slate-400 hover:text-slate-700" onClick={()=>setFiles(files.filter((_,j)=>j!==i))}><X className="w-3 h-3"/></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={!f.description} data-testid="new-wo-save">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200"><CardContent className="p-4">
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <Table className="dense-table">
            <TableHeader><TableRow>
              <TableHead>WO #</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Cost</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((w)=>{
                const a = assets.find((x)=>x.id===w.asset_id);
                const s = suppliers.find((x)=>x.id===w.assigned_supplier_id);
                return (
                  <TableRow key={w.id} data-testid={`wo-row-${w.id}`}>
                    <TableCell className="font-mono text-xs">{w.wo_number}</TableCell>
                    <TableCell><div className="text-sm text-slate-900 max-w-md">{w.description}</div></TableCell>
                    <TableCell className="text-xs">{a?.name||"—"}</TableCell>
                    <TableCell className="text-xs">{s?.name||"—"}</TableCell>
                    <TableCell><Badge variant="outline">{w.priority}</Badge></TableCell>
                    <TableCell>{w.is_regulatory ? <Badge className="bg-red-50 text-red-700 border-red-200">Regulatory</Badge> : <Badge variant="outline">General</Badge>}</TableCell>
                    <TableCell>
                      <Select value={w.status} onValueChange={(v)=>setStatus(w.id, v)}>
                        <SelectTrigger className="h-8 w-40 text-xs" data-testid={`wo-status-${w.id}`}><SelectValue/></SelectTrigger>
                        <SelectContent>{STATUS.map((s)=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs">{w.total_cost != null ? `$${Number(w.total_cost).toLocaleString()}` : "—"}</TableCell>
                  </TableRow>
                );
              })}
              {rows.length===0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No work orders.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>
    </div>
  );
}
